
import {Component} from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import {GobiColor} from '@/helpers/color';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        StackedBarChartFilterable,
    },
})
export default class SalesOrderConversionRateBarChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public filterIds: Array<
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'areas'
    > = ['agents', 'customers', 'areas'];

    public xAxisData: string[] = [];
    public finalDatas: Array<{
        name: string;
        openAmount: number;
        convertedAmount: number;
        totalAmount: number;
        conversionRate: number;
    }> = [];
    public legends: string[] = [];
    public series: echarts.ECharts[] | any = [];

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get expensiveHook() {
        const {
            selectedAgents,
            selectedCustomers,
            selectedAreas,
        } = this;
        return JSON.stringify([
            selectedAgents,
            selectedCustomers,
            selectedAreas,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        const salesOrderTable = getTableName('salesOrder');

        // Ensure all cross-table dependencies are loaded
        const queryHead = await this._ensureTablesLoaded(['salesOrder', 'globalCustomers']);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Build filter clauses manually
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

        let agentFilter = '';
        if (this.selectedAgents.length > 0) {
            const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
            agentFilter = ` AND ${COL.AGENT} IN (${list})`;
        }

        const globalCustomersTable = getTableName('globalCustomers');
        let filterClause = '';

        if (this.selectedAreas.length > 0) {
            const areaList = this.selectedAreas.map((a) => `'${escapeStr(a)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (
                SELECT "CODE" FROM ${globalCustomersTable} WHERE "AREA" IN (${areaList})
            )`;
        }

        if (this.selectedCustomers.length > 0) {
            const customerList = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (${customerList})`;
        }

        // Single query: amounts and outstanding per customer
        const data = await queryHead.rawQuery(`
            SELECT ${COL.CODE} as code,
                SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)) as amount,
                SUM(CASE WHEN TRY_CAST(${COL.QUANTITY} AS DOUBLE) > 0
                    THEN (TRY_CAST(${COL.AMOUNT} AS DOUBLE)
                        / TRY_CAST(${COL.QUANTITY} AS DOUBLE))
                        * TRY_CAST("OSQTY" AS DOUBLE)
                    ELSE 0 END) as osAmount
            FROM ${salesOrderTable}
            WHERE 1=1${agentFilter}${filterClause}
            GROUP BY ${COL.CODE}
        `);

        const customerData: Array<{
            customer: string;
            amount: number;
            osAmount: number;
        }> = [];

        for (const row of data) {
            const osAmount = Number(row.osAmount) || 0;
            if (osAmount > 0) {
                customerData.push({
                    customer: gdbx.customerNames[String(row.code)]
                        || String(row.code),
                    amount: Number(row.amount) || 0,
                    osAmount,
                });
            }
        }

        // Sort by outstanding amount descending
        customerData.sort((a, b) => b.osAmount - a.osAmount);

        this.xAxisData = customerData.map((item) => item.customer);

        const settled = {
            name: 'Settled Amount',
            type: 'bar',
            barWidth: '40%',
            color: GobiColor.COLOR1,
            stack: 'a',
            data: customerData.map((dd) => dd.amount - dd.osAmount),
        };

        const outstanding = {
            name: 'Outstanding Amount',
            type: 'bar',
            barWidth: '40%',
            color: GobiColor.COLOR2,
            stack: 'a',
            data: customerData.map((dd) => dd.osAmount),
        };

        const total = {
            name: 'Total',
            type: 'bar',
            barWidth: '10%',
            color: GobiColor.COLOR4,
            data: customerData.map((dd) => dd.amount),
        };

        this.generateLoadingText(1);

        this.legends = ['Outstanding Amount', 'Settled Amount', 'Total'];
        this.series = [outstanding, settled, total];

        this.saveHistory('series', 'xAxisData', 'legends', 'finalDatas');
    }
}
